/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Action, NgxsAfterBootstrap, State, StateContext, StateToken, createSelector } from '@ngxs/store';
import { iif, insertItem, updateItem } from '@ngxs/store/operators';
import { SetConsent, SetConsents } from '@stores/consents/consents.actions';
import { ConsentInterface } from '@wizbii/utils/models';
import { fromEvent } from 'rxjs';

const ConsentsStateToken = new StateToken<ConsentInterface[]>('consents');

@State({
  name: ConsentsStateToken,
  defaults: [],
})
@Injectable()
export class ConsentsState implements NgxsAfterBootstrap {
  readonly #document: any = inject(DOCUMENT);

  static consent(key: string) {
    return createSelector([ConsentsStateToken], (state: ConsentInterface[]) =>
      state.find((consent) => consent.key === key)
    );
  }

  ngxsAfterBootstrap(ctx?: StateContext<any>) {
    // Listen for all consents changed at once
    fromEvent<{ WizbiiGdpr: { consent: ConsentInterface[] } }>(this.#document, 'WizbiiGdpr.consentsChange').subscribe(
      ({ WizbiiGdpr: { consent } }) => {
        ctx.dispatch(new SetConsents(consent));
      }
    );

    // Listen for a consent changed on its own
    fromEvent<{ WizbiiGdpr: { consent: ConsentInterface } }>(this.#document, 'WizbiiGdpr.consentChange').subscribe(
      ({ WizbiiGdpr: { consent } }) => {
        ctx.dispatch(new SetConsent(consent));
      }
    );
  }

  @Action(SetConsents)
  setConsents(ctx: StateContext<ConsentInterface[]>, { consents }: SetConsents) {
    const keyTest = Object.keys(consents)[0];

    if (Array.isArray(consents)) {
      ctx.setState(consents);
    } else if (Array.isArray(consents[keyTest])) {
      ctx.setState(Object.values(consents).flat());
    } else {
      ctx.setState(Object.keys(consents).map((key) => ({ key, value: consents[key] })) as any);
    }
  }

  @Action(SetConsent)
  setConsent(ctx: StateContext<ConsentInterface[]>, { consent }: SetConsent) {
    ctx.setState(
      iif(
        (state) => !!state.find((stateConsent) => stateConsent.key === consent.key),
        updateItem((stateConsent) => stateConsent.key === consent.key, consent),
        insertItem(consent)
      )
    );
  }
}
